import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Clock, ChevronLeft, ChevronRight, Download, FileText, Lock, Unlock,
  AlertTriangle, X, Keyboard,
} from "lucide-react";
import API from "../services/api";
import Grid from "./timesheets/Grid";
import DayEditor from "./timesheets/DayEditor";
import ManualEntry from "./timesheets/ManualEntry";
import LogNinjaSync from "./timesheets/LogNinjaSync";
import { downloadWithAuth, errMsg, nzToday, addDay, prettyDate } from "./runsheets/util";
import {
  currentFortnight, fortnightDays, buildRows, exceptionList,
  CELL_TINT, FLAG_TINT, LEGAL_LINE, fmtHours,
} from "./timesheets/util";

/* The fortnight, end to end: what everyone worked, what needs looking at,
 * what still has to be keyed in, and the sign-off that locks it.
 *
 * This screen is a PAY record. It is not a logbook and must never be read as
 * one - a logbook is prescribed by Land Transport Rule: Work Time and
 * Logbooks 2007 and an electronic one has to be approved by NZTA and Police.
 * Hours flow logbook -> here, never the other way, and nothing on this page
 * says "compliant": the most it can say is that the hours recorded here
 * raised no flag.
 */
export default function Timesheets() {
  const [end, setEnd] = useState(() => currentFortnight(nzToday()).end);
  const [periods, setPeriods] = useState([]);
  const [crew, setCrew] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [editing, setEditing] = useState(null);      // { personId, day }
  const [showEntry, setShowEntry] = useState(false);
  // A break is recorded as its start and finish, never seeded: blank means no
  // break, and a break the office did not actually record must not appear.
  const [entryDraft, setEntryDraft] = useState(() => ({
    person_id: "", work_date: nzToday(), start: "07:00", finish: "17:00",
    break_start: "", break_finish: "", day_type: "normal", note: "",
  }));

  const start = useMemo(() => addDay(end, -13), [end]);
  const days = useMemo(() => fortnightDays(start), [start]);
  const snapped = useRef(false);

  const reportError = useCallback((err) => setError(errMsg(err)), []);
  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(null), 6000);
    return () => clearTimeout(t);
  }, [notice]);

  const loadPeriods = useCallback(async () => {
    try {
      const res = await API.get("/time/periods?count=14");
      const rows = Array.isArray(res.data) ? res.data : [];
      setPeriods(rows);
      // The server owns the fortnight anchor. Snap to the period it calls
      // current the first time it answers, so a client that disagrees about
      // the anchor never shows the operator a window payroll does not use.
      if (!snapped.current && rows.length) {
        snapped.current = true;
        if (rows[0].end) setEnd(rows[0].end);
      }
    } catch (err) {
      reportError(err);
    }
  }, [reportError]);

  const loadCrew = useCallback(async () => {
    try {
      const res = await API.get("/drivers");
      setCrew(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      reportError(err);
    }
  }, [reportError]);

  const loadShifts = useCallback(async () => {
    try {
      const res = await API.get(`/time/shifts?from=${start}&to=${end}`);
      setShifts(Array.isArray(res.data?.shifts) ? res.data.shifts : []);
      setTotals(res.data?.totals || null);
    } catch (err) {
      reportError(err);
    }
  }, [start, end, reportError]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([loadCrew(), loadPeriods()]).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [loadCrew, loadPeriods]);

  useEffect(() => { loadShifts(); }, [loadShifts]);

  const refresh = useCallback(async () => {
    await Promise.all([loadShifts(), loadPeriods()]);
  }, [loadShifts, loadPeriods]);

  const rows = useMemo(() => buildRows(crew, shifts, days), [crew, shifts, days]);
  const exceptions = useMemo(() => exceptionList(shifts), [shifts]);
  const period = useMemo(() => periods.find((p) => p.end === end) || null, [periods, end]);
  // A fortnight the server has never written a row for is open - that is
  // exactly how the API reads it too.
  const status = period?.status || "open";
  const approved = status === "approved";

  const editPerson = editing ? rows.find((r) => String(r.id) === String(editing.personId)) : null;
  const editShifts = editing
    ? shifts.filter((s) => String(s.person_id) === String(editing.personId)
        && String(s.work_date || "").slice(0, 10) === editing.day)
    : [];

  const act = async (fn, message) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      if (message) setNotice(message);
    } catch (err) {
      reportError(err);
    } finally {
      setBusy(false);
    }
  };

  const approve = () => {
    if (!window.confirm(
      `Approve the fortnight ${start} to ${end}?\n\n`
      + "This LOCKS every day in it: drivers and offsiders can no longer change "
      + "their own hours, and only an admin can reopen it. Every lock is written "
      + "to the audit trail.")) return;
    act(async () => {
      const res = await API.post("/time/periods/approve", { end_date: end });
      await refresh();
      setNotice(`Fortnight approved — ${res.data?.shifts_locked ?? 0} shifts locked.`);
    });
  };

  const reopen = () => {
    if (!window.confirm(
      `Reopen the fortnight ${start} to ${end}?\n\n`
      + "Crew will be able to change their own hours again. Reopening an "
      + "approved pay period is recorded against every shift in it.")) return;
    act(async () => {
      const res = await API.post("/time/periods/reopen", { end_date: end });
      await refresh();
      setNotice(`Fortnight reopened — ${res.data?.shifts_unlocked ?? 0} shifts unlocked.`);
    });
  };

  // Plain <a href> downloads carry no Authorization header and 401 under the
  // auth middleware, so both exports go through the blob helper.
  const download = (kind, query) => act(
    () => downloadWithAuth(`/time/fortnight.${kind}?end=${end}${query || ""}`,
      `timesheet_${start}_${end}.${kind}`),
    `Downloaded the ${kind.toUpperCase()} for ${start} to ${end}.`);

  const openDay = (row, day) => {
    setEditing({ personId: row.id, day });
    setEntryDraft((d) => ({ ...d, person_id: String(row.id), work_date: day }));
  };

  if (loading) return <div className="p-8 text-center text-slate-400">Loading timesheets…</div>;

  return (
    <div className="max-w-[1500px] mx-auto space-y-5">
      {/* Header */}
      <div className="border-b border-slate-800 pb-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-black text-white tracking-tight flex items-center gap-2">
            <Clock size={22} className="text-indigo-400" /> Timesheets
          </h2>
          <p className="text-slate-400 text-xs mt-1">
            Paid hours by fortnight. Meal breaks are paid — break start and finish times are recorded for work-time visibility and are never deducted.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button onClick={() => setEnd(addDay(end, -14))} disabled={busy}
            className="p-2 rounded-lg bg-slate-900/60 border border-slate-800 text-slate-300 hover:text-white hover:border-slate-600 transition-all disabled:opacity-50"
            title="Previous fortnight">
            <ChevronLeft size={16} />
          </button>
          <div className="text-center px-3">
            <div className="text-sm font-bold text-white whitespace-nowrap">{prettyDate(start)} → {prettyDate(end)}</div>
            <div className="flex items-center justify-center gap-2 mt-0.5">
              <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${approved
                ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10"
                : "text-sky-300 border-sky-500/40 bg-sky-500/10"}`}>
                {status}
              </span>
              {approved && period?.approved_by && (
                <span className="text-[10px] text-slate-500">by {period.approved_by}</span>
              )}
            </div>
          </div>
          <button onClick={() => setEnd(addDay(end, 14))} disabled={busy}
            className="p-2 rounded-lg bg-slate-900/60 border border-slate-800 text-slate-300 hover:text-white hover:border-slate-600 transition-all disabled:opacity-50"
            title="Next fortnight">
            <ChevronRight size={16} />
          </button>
          <button onClick={() => setEnd(periods[0]?.end || currentFortnight(nzToday()).end)}
            className="text-[11px] text-slate-400 hover:text-white underline ml-1 whitespace-nowrap">
            This fortnight
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-sm text-red-300 flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span className="flex-1 break-words">{error}</span>
          <button onClick={() => setError(null)} className="text-red-300/70 hover:text-white"><X size={14} /></button>
        </div>
      )}
      {notice && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 text-sm text-emerald-300 flex items-center gap-2">
          <span className="flex-1">{notice}</span>
          <button onClick={() => setNotice(null)} className="text-emerald-300/70 hover:text-white"><X size={14} /></button>
        </div>
      )}

      {/* Totals and the actions that close the fortnight off */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-950/40 rounded-2xl border border-slate-800/80 p-4">
        <div className="flex flex-wrap items-center gap-5 text-xs">
          <div>
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">Paid hours</div>
            <div className="text-lg font-black text-white font-mono">{fmtHours(totals?.paid_hours || 0)}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">Crew with hours</div>
            <div className="text-lg font-black text-white font-mono">{new Set(shifts.map((s) => s.person_id)).size}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">Over limit</div>
            <div className={`text-lg font-black font-mono ${totals?.over_limit ? "text-red-400" : "text-slate-500"}`}>{totals?.over_limit || 0}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">Check</div>
            <div className={`text-lg font-black font-mono ${totals?.check ? "text-amber-400" : "text-slate-500"}`}>{totals?.check || 0}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">Keyed in</div>
            <div className={`text-lg font-black font-mono ${totals?.manual ? "text-violet-400" : "text-slate-500"}`}>{totals?.manual || 0}</div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => download("xlsx")} disabled={busy}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-slate-900/60 border border-slate-800 text-slate-200 hover:border-slate-600 transition-all disabled:opacity-50">
            <Download size={14} /> Workbook (.xlsx)
          </button>
          <button onClick={() => download("pdf", "&all=1")} disabled={busy}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-slate-900/60 border border-slate-800 text-slate-200 hover:border-slate-600 transition-all disabled:opacity-50">
            <FileText size={14} /> Signable sheets (.pdf)
          </button>
          {approved ? (
            <button onClick={reopen} disabled={busy}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold bg-amber-600 hover:bg-amber-500 text-white transition-all disabled:opacity-50">
              <Unlock size={14} /> Reopen fortnight
            </button>
          ) : (
            <button onClick={approve} disabled={busy}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold bg-emerald-600 hover:bg-emerald-500 text-white transition-all disabled:opacity-50">
              <Lock size={14} /> Approve fortnight
            </button>
          )}
        </div>
      </div>

      {/* Drivers' logbook days from Log Ninja. A finished run has written
          shifts the grid does not know about yet, so it reloads them. */}
      <LogNinjaSync onApplied={() => { loadShifts(); }} />

      {/* Grid */}
      <Grid rows={rows} days={days} onPickCell={openDay} />

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-[10px] text-slate-400">
        <span className="flex items-center gap-1.5"><span className={`w-3 h-3 rounded border ${CELL_TINT.red}`} /> OVER LIMIT</span>
        <span className="flex items-center gap-1.5"><span className={`w-3 h-3 rounded border ${CELL_TINT.amber}`} /> CHECK</span>
        <span className="flex items-center gap-1.5"><span className={`w-3 h-3 rounded border ${CELL_TINT.manual}`} /> keyed in by the office</span>
        <span className="text-slate-500">Click any cell to open that day. Work-time flags are raised for drivers only — an offsider is not driving.</span>
      </div>

      {/* Manual entry */}
      <div className="bg-slate-950/40 rounded-2xl border border-slate-800/80 p-5 space-y-3">
        <button onClick={() => setShowEntry(!showEntry)}
          className="w-full flex items-center justify-between text-sm font-bold text-white">
          <span className="flex items-center gap-2"><Keyboard size={16} className="text-indigo-400" /> Manual entry</span>
          <span className="text-[11px] font-normal text-slate-400">{showEntry ? "hide" : "open"}</span>
        </button>
        {showEntry && (
          <ManualEntry crew={crew} draft={entryDraft} setDraft={setEntryDraft}
            onChanged={refresh} periodStatus={status} />
        )}
      </div>

      {/* Exceptions to work before signing the fortnight off */}
      {exceptions.length > 0 && (
        <div className="bg-slate-950/40 rounded-2xl border border-slate-800/80 p-5 space-y-2">
          <h3 className="font-bold text-white text-sm border-b border-slate-800 pb-2">
            Flags on the hours recorded here ({exceptions.length})
          </h3>
          {exceptions.map((f, i) => (
            <button key={`${f.shift.id}-${f.code}-${i}`}
              onClick={() => setEditing({ personId: f.shift.person_id, day: String(f.shift.work_date).slice(0, 10) })}
              className={`w-full text-left text-[11px] rounded-lg border px-3 py-2 hover:brightness-125 transition-all ${FLAG_TINT[f.tier] || FLAG_TINT.info}`}>
              <span className="font-bold">{f.shift.person_name || "Crew"}</span>
              <span className="opacity-70"> · {f.shift.work_date} · </span>
              <span className="font-bold">{f.code}</span> {f.message}
            </button>
          ))}
        </div>
      )}

      {/* The line that has to be on the product, not just in the code. */}
      <p className="text-[10px] text-slate-500 text-center pb-2">{LEGAL_LINE}</p>

      {editing && editPerson && (
        <DayEditor person={editPerson} day={editing.day} shifts={editShifts}
          periodStatus={status} onClose={() => setEditing(null)} onChanged={refresh} />
      )}
    </div>
  );
}
